import { selectIsOfferRescinding } from 'modules/offerLetter/store/selectors';
import React, { useCallback } from 'react';
import { Box, Button } from '@material-ui/core';
import ButtonWithLoader from 'shared/components/buttons/ButtonWithLoader';
import CheckboxField from 'shared/components/formFields/CheckboxField';
import { IOfferLetter, OfferLetterRescissionReason } from '../../store/model';
import { useOfferLetterStyles } from '../../OfferLetterStyles';
import { Formik } from 'formik';
import * as yup from 'yup';
import { ValidationMessages } from 'shared/models/Validation';
import OfferLetterRescissionPanel from './OfferLetterRescissionPanel';
import { makeHighPriorityStyles } from 'utils/stylesWrapper';
import { rescindOffer } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';

export interface IOfferLetterRescissionContentProp {
    offerLetter: IOfferLetter;
    onCancel: () => void;
    id: string;
}

const rescissionOfferLetterValidationSchema = yup.object().shape({
    rescissionReason: yup.string().required(ValidationMessages.REQUIRED),
    rescissionReasonText: yup.string().trim(),
});

const useOfferLetterRescissionFormStyles = makeHighPriorityStyles(theme => ({
    form: {
        width: '100%',
    },
    formRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    inlineInputLabel: {
        fontWeight: 'bold',
        paddingRight: theme.spacing(2),
    },
}));

export interface IOfferLetterRescissionFormValues {
    rescissionReason: OfferLetterRescissionReason | '';
    rescissionReasonText: string;
    doNotSendEmployeeEmail: boolean;
}

function OfferLetterRescissionContent({ offerLetter, onCancel, id }: IOfferLetterRescissionContentProp) {
    const classes = useOfferLetterStyles();
    const dispatch = useDispatch();
    const isLoading = useSelector(selectIsOfferRescinding);
    const onSubmit = useCallback(
        (values: IOfferLetterRescissionFormValues) => {
            dispatch(rescindOffer.init({ id, values }));
        },
        [id, dispatch],
    );
    const localClasses = useOfferLetterRescissionFormStyles();
    return (
        <Box component="section" p={2}>
            <Box>
                You are <b>rescinding</b> this offer letter. Please enter the reason why you are revoking it below.
            </Box>
            <Formik
                initialValues={{
                    rescissionReason: '',
                    rescissionReasonText: '',
                    doNotSendEmployeeEmail: false,
                }}
                validationSchema={rescissionOfferLetterValidationSchema}
                onSubmit={onSubmit}
                validateOnBlur={false}
            >
                {formProps => (
                    <form onSubmit={formProps.handleSubmit} className={localClasses.form}>
                        <Box>
                            <OfferLetterRescissionPanel
                                offerLetter={offerLetter}
                                hasCustomReason={
                                    formProps.values.rescissionReason === OfferLetterRescissionReason.Other
                                }
                            />
                        </Box>
                        <Box className={localClasses.formRow} mb={3}>
                            <label htmlFor="doNotSendEmployeeEmail" className={localClasses.inlineInputLabel}>
                                Do not send email
                            </label>
                            <CheckboxField name="doNotSendEmployeeEmail" />
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            mt={3}
                        >
                            <Box className={classes.buttonWrapper}>
                                <ButtonWithLoader
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={isLoading}
                                    isLoading={isLoading}
                                    data-testid="submit_button"
                                >
                                    Confirm
                                </ButtonWithLoader>
                                <Button
                                    className={classes.secondaryButton}
                                    variant="text"
                                    onClick={onCancel}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
}

export default OfferLetterRescissionContent;
